import React, { useState, useEffect, useCallback } from "react";
import api from "@/utils/api";
import { Trophy, TrendingUp, Star, Zap } from "lucide-react";

/**
 * PointsWidget - Real-time banner showing Today + This Month points
 * Polls backend every 30 seconds for live updates
 * 
 * Usage:
 * <PointsWidget />
 * 
 * Displays:
 * - Points earned today
 * - Points earned this month
 * - Task count
 * - Auto-refreshes after task submissions
 */
const PointsWidget = ({ className = "", refreshTrigger = 0 }) => {
  const [points, setPoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchPoints = useCallback(async () => {
    try {
      const res = await api.get("/gamification/points/my-points");
      setPoints(res.data);
      setError(null);
    } catch (e) {
      console.error("Failed to fetch points:", e);
      setError("Unable to load points");
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchPoints();
    
    // Poll every 30 seconds
    const interval = setInterval(fetchPoints, 30000);
    
    return () => clearInterval(interval);
  }, [fetchPoints]);

  // Refresh when trigger changes (e.g., after task submission)
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchPoints();
    }
  }, [refreshTrigger, fetchPoints]);

  // Don't show widget for excluded roles
  if (points?.excluded) {
    return null;
  }

  if (loading && !points) {
    return (
      <div className={`bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-4 text-white animate-pulse ${className}`}>
        <div className="h-16 flex items-center justify-center">
          <span className="text-sm opacity-80">Loading points...</span>
        </div>
      </div>
    );
  }

  if (error && !points) {
    return null; // Silently fail if points can't be loaded
  }

  return (
    <div 
      className={`bg-gradient-to-r from-orange-500 to-amber-500 rounded-lg p-4 text-white shadow-lg ${className}`}
      data-testid="points-widget"
    >
      <div className="flex items-center justify-between">
        {/* Left: Today's Points */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Zap size={24} className="text-yellow-200" />
          </div>
          <div>
            <p className="text-xs font-medium text-orange-100 uppercase tracking-wider">Today</p>
            <p className="text-3xl font-black" style={{ fontFamily: "Manrope" }} data-testid="points-today">
              {points?.points_today?.toFixed(1) || "0.0"}
            </p>
            <p className="text-xs text-orange-100">
              {points?.task_count_today || 0} tasks
            </p>
          </div>
        </div>

        {/* Divider */}
        <div className="h-14 w-px bg-white/30 mx-4 hidden sm:block" />

        {/* Right: This Month */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
            <Trophy size={24} className="text-yellow-200" />
          </div>
          <div>
            <p className="text-xs font-medium text-orange-100 uppercase tracking-wider">This Month</p>
            <p className="text-3xl font-black" style={{ fontFamily: "Manrope" }} data-testid="points-month">
              {points?.points_this_month?.toFixed(1) || "0.0"}
            </p>
            <p className="text-xs text-orange-100">
              {points?.days_worked_this_month || 0} days active
            </p>
          </div>
        </div>

        {/* Optional: Distance Bonus indicator */}
        {points?.distance_bonus_today > 0 && (
          <>
            <div className="h-14 w-px bg-white/30 mx-4 hidden md:block" />
            <div className="hidden md:flex items-center gap-2">
              <TrendingUp size={20} className="text-green-200" />
              <div>
                <p className="text-xs text-orange-100">Distance Bonus</p>
                <p className="text-lg font-bold">+{points.distance_bonus_today}</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PointsWidget;
